import { useAuth0 } from '@auth0/auth0-react';
import { Avatar, Box, Divider, Menu, Typography } from '@mui/material';
import DevsIcon from 'assets/img/icons/DevsIcon';
import EuroIcon from 'assets/img/icons/EuroIcon';
import OldFileIcon from 'assets/img/icons/OldFileIcon';
import InfoIcon from 'assets/img/icons/InfoIcon';
import LogoutIcon from 'assets/img/icons/LogoutIcon';
import { resetOnLogout } from 'core/store/claimsV2Slice';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { DARKER_GREY } from 'utils/theme';
import { UserMenuStyles } from './UserMenuStyles';
import MenuOpenButton from './components/MenuOpenButton';
import UserMenuItem from './components/UserMenuItem';
import HomeIcon from 'assets/img/icons/HomeIcon';
import ThumbsUpIcon from 'assets/img/icons/ThumbsUpIcon';

interface UserMenuProps {
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ isOpen, setIsOpen }) => {
  const { logout, user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const handleLogout = useCallback(() => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    setAnchorEl(null);
    setIsOpen(false);
    dispatch(resetOnLogout());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isOpen && !anchorEl) {
      const button = buttonRef.current;
      if (button) {
        setAnchorEl(button);
      }
    }
  }, [isOpen, anchorEl]);

  const userMenuItems = [
    {
      title: 'Home',
      onClick: () => {
        handleClose(), navigate('/playgroundV2');
      },
      icon: <HomeIcon />,
      show: location.pathname !== '/playgroundV2' && location.pathname !== '/',
    },
    {
      title: 'Old design',
      onClick: () => {
        handleClose(), navigate('/home');
      },
      icon: <OldFileIcon />,
    },
    {
      title: 'FAQ and support',
      onClick: () => {
        handleClose(), navigate('/faq');
      },
      icon: <InfoIcon />,
    },
    {
      title: 'Pricing',
      onClick: () => {
        handleClose(), navigate('/pricing');
      },
      icon: <EuroIcon />,
    },
    {
      title: 'Developers',
      onClick: () => {
        navigate('/developers');
        handleClose();
      },
      icon: <DevsIcon />,
      show: isAuthenticated,
    },
    {
      title: 'Send Feedback',
      onClick: () => {
        const email = user?.email;
        if (!email) return;

        // Construct the hash with the email as a hidden field
        const hash = `#tally-open=3xByg9&email=${encodeURIComponent(
          email
        )}&tally-emoji-text=👋&tally-emoji-animation=wave`;

        // Create a temporary link and click it
        const a = document.createElement('a');
        a.href = hash;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      },
      icon: <ThumbsUpIcon />,
      show: isAuthenticated,
    },
    {
      title: 'Logout',
      onClick: handleLogout,
      icon: <LogoutIcon />,
      show: isAuthenticated,
    },
  ];

  return (
    <Box>
      <MenuOpenButton ref={buttonRef} onClick={handleClick} isOpen={isOpen} />
      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: UserMenuStyles,
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {isAuthenticated && (
          <Box>
            <UserMenuItem
              title={
                <Typography
                  color={DARKER_GREY}
                  fontWeight="medium"
                  sx={{ opacity: 0.7 }}
                  fontSize="16px"
                >
                  {user?.name}
                </Typography>
              }
              icon={<Avatar src={user?.picture} />}
              onClick={handleClose}
            />
            <Divider />
          </Box>
        )}

        {userMenuItems.map(({ title, onClick, icon, show = true }, i) => {
          return show && <UserMenuItem key={i} title={title} onClick={onClick} icon={icon} />;
        })}
      </Menu>
    </Box>
  );
};

export default UserMenu;
